/**
 * Utilitários para manipulação de mapas no módulo de postes de iluminação
 */

import L from 'leaflet';

/**
 * Cria um ícone personalizado para postes de iluminação baseado no status
 * @param {string} status - Status do poste ('Funcionando', 'Alerta', 'Defeito')
 * @returns {L.DivIcon} Ícone personalizado do Leaflet
 */
export const createPosteIcon = (status) => {
  let color;
  
  // Determina a cor com base no status
  switch (status) {
    case 'Funcionando':
      color = '#4caf50'; // Verde
      break;
    case 'Alerta':
      color = '#ff9800'; // Amarelo/Laranja
      break;
    case 'Defeito':
      color = '#f44336'; // Vermelho
      break;
    default:
      color = '#757575'; // Cinza para status desconhecido
  }
  
  const size = 20;
  
  // Ícone de poste simplificado mas reconhecível
  const posteIcon = `
    <div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <!-- Base do poste -->
      <div style="
        position: absolute;
        bottom: 2px;
        left: 50%;
        transform: translateX(-50%);
        width: 3px;
        height: 14px;
        background: #424242;
        border-radius: 1px;
      "></div>
      
      <!-- Braço do poste -->
      <div style="
        position: absolute;
        top: 4px;
        left: 50%;
        transform: translateX(-50%);
        width: 8px;
        height: 2px;
        background: #424242;
        border-radius: 1px;
      "></div>
      
      <!-- Luminária -->
      <div style="
        position: absolute;
        top: 2px;
        right: 4px;
        width: 6px;
        height: 6px;
        background: ${color};
        border-radius: 2px;
        border: 1px solid white;
        box-shadow: 0 1px 2px rgba(0,0,0,0.3);
      "></div>
      
      <!-- Indicador de status na base -->
      <div style="
        position: absolute;
        bottom: 0px;
        left: 50%;
        transform: translateX(-50%);
        width: 6px;
        height: 2px;
        background: ${color};
        border-radius: 1px;
        border: 1px solid white;
      "></div>
    </div>
  `;
  
  return L.divIcon({
    className: 'poste-marker',
    html: posteIcon,
    iconSize: [size, size],
    iconAnchor: [size / 2, size - 2],
    popupAnchor: [0, -size + 2]
  });
};

/**
 * Calcula o centro do mapa baseado nos postes disponíveis
 * @param {Array} postes - Array de objetos de postes com latitude e longitude
 * @returns {Array} Coordenadas do centro [latitude, longitude]
 */
export const calculateMapCenter = (postes) => {
  // Fallback para coordenadas de Conceição do Castelo - ES se não há postes
  if (!postes || postes.length === 0) {
    return [-20.359292, -41.247282];
  }
  
  // Filtra postes com coordenadas válidas
  const postesValidos = postes.filter(poste => 
    poste.latitude && 
    poste.longitude && 
    !isNaN(parseFloat(poste.latitude)) && 
    !isNaN(parseFloat(poste.longitude))
  );
  
  if (postesValidos.length === 0) {
    return [-20.359292, -41.247282];
  }
  
  // Calcula a média das coordenadas
  let totalLat = 0;
  let totalLng = 0;
  
  postesValidos.forEach(poste => {
    totalLat += parseFloat(poste.latitude);
    totalLng += parseFloat(poste.longitude);
  });
  
  return [totalLat / postesValidos.length, totalLng / postesValidos.length];
};

/**
 * Formata coordenadas para exibição
 * @param {number} coordenada - Valor da coordenada
 * @param {number} casasDecimais - Número de casas decimais (padrão: 6)
 * @returns {string} Coordenada formatada
 */
export const formatarCoordenada = (coordenada, casasDecimais = 6) => {
  if (!coordenada || isNaN(parseFloat(coordenada))) {
    return '';
  }
  
  const num = parseFloat(coordenada);
  return num.toFixed(casasDecimais);
};

/**
 * Formata um par de coordenadas para exibição
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @param {number} casasDecimais - Número de casas decimais (padrão: 6)
 * @returns {string} Coordenadas formatadas como "lat, lng"
 */
export const formatarCoordenadas = (latitude, longitude, casasDecimais = 6) => {
  const latFormatada = formatarCoordenada(latitude, casasDecimais);
  const lngFormatada = formatarCoordenada(longitude, casasDecimais);
  
  if (!latFormatada || !lngFormatada) {
    return '';
  }
  
  return `${latFormatada}, ${lngFormatada}`;
};

/**
 * Calcula a distância entre dois pontos em metros usando a fórmula de Haversine
 * @param {Array} coord1 - Coordenadas do ponto 1 [latitude, longitude]
 * @param {Array} coord2 - Coordenadas do ponto 2 [latitude, longitude]
 * @returns {number} Distância em metros
 */
export const calcularDistancia = (coord1, coord2) => {
  if (!coord1 || !coord2 || coord1.length < 2 || coord2.length < 2) {
    return 0;
  }
  
  const lat1 = parseFloat(coord1[0]);
  const lon1 = parseFloat(coord1[1]);
  const lat2 = parseFloat(coord2[0]);
  const lon2 = parseFloat(coord2[1]);
  
  if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) {
    return 0;
  }
  
  const R = 6371e3; // Raio da Terra em metros
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;
  
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distância em metros
};

/**
 * Formata distância para exibição legível
 * @param {number} metros - Distância em metros
 * @returns {string} Distância formatada (ex: "1.5 km" ou "250 m")
 */
export const formatarDistancia = (metros) => {
  if (!metros || isNaN(metros)) {
    return '0 m';
  }
  
  if (metros >= 1000) {
    return `${(metros / 1000).toFixed(2)} km`;
  }
  
  return `${Math.round(metros)} m`;
};

/**
 * Calcula o zoom adequado para exibir todos os postes
 * @param {Array} postes - Array de postes com coordenadas
 * @param {L.Map} map - Instância do mapa Leaflet (opcional)
 * @returns {number} Nível de zoom recomendado
 */
export const calcularZoomAdequado = (postes, map = null) => {
  if (!postes || postes.length <= 1) {
    return 13; // Zoom padrão para a cidade
  }
  
  // Filtra postes com coordenadas válidas
  const postesValidos = postes.filter(poste => 
    poste.latitude && 
    poste.longitude && 
    !isNaN(parseFloat(poste.latitude)) && 
    !isNaN(parseFloat(poste.longitude))
  );
  
  if (postesValidos.length <= 1) {
    return 13;
  }
  
  // Se temos uma instância do mapa, usa fitBounds
  if (map) {
    const coordenadas = postesValidos.map(poste => [
      parseFloat(poste.latitude),
      parseFloat(poste.longitude)
    ]);
    
    const bounds = L.latLngBounds(coordenadas);
    map.fitBounds(bounds, { padding: [20, 20] });
    return map.getZoom();
  }
  
  // Cálculo manual baseado na dispersão dos pontos
  const lats = postesValidos.map(p => parseFloat(p.latitude));
  const lngs = postesValidos.map(p => parseFloat(p.longitude));
  
  const latRange = Math.max(...lats) - Math.min(...lats);
  const lngRange = Math.max(...lngs) - Math.min(...lngs);
  const maxRange = Math.max(latRange, lngRange);
  
  // Zoom baseado na dispersão (aproximação)
  if (maxRange > 0.1) return 10;
  if (maxRange > 0.05) return 11;
  if (maxRange > 0.02) return 12;
  if (maxRange > 0.01) return 13;
  if (maxRange > 0.005) return 14;
  return 15;
};

/**
 * Valida se as coordenadas estão dentro dos limites válidos
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {boolean} True se as coordenadas são válidas
 */
export const validarCoordenadas = (latitude, longitude) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  
  if (isNaN(lat) || isNaN(lng)) {
    return false;
  }
  
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};